import { DateTime } from "luxon";
import {
  ENCOUNTER_CREATION_SOURCE_LABELS,
  EncounterCreationSource,
} from "./encounter-created-nurse-note.util";

export const SCHEDULE_CREATED_ACTIVITY_TYPE = "Schedule Created";
export const SCHEDULE_UPDATED_ACTIVITY_TYPE = "Schedule Updated";

export type ScheduleActivitySource =
  | "clinic-encounter-schedule"
  | "clinic-encounter-schedule-assistant"
  | EncounterCreationSource;

export const SCHEDULE_ACTIVITY_SOURCE_LABELS: Record<
  ScheduleActivitySource,
  string
> = {
  "clinic-encounter-schedule": "Clinic — Encounter Schedule",
  "clinic-encounter-schedule-assistant": "Clinic — Schedule Assistant",
  ...ENCOUNTER_CREATION_SOURCE_LABELS,
};

export interface ScheduleActivityDetails {
  scheduleDate?: string;
  checkIn?: string;
  visitType?: string;
  location?: string;
  resourceName?: string;
  previousScheduleDate?: string;
  previousCheckIn?: string;
}

export function buildScheduleActivityNoteSummary(input: {
  createdBy: string;
  isUpdate: boolean;
  source: ScheduleActivitySource;
  details: ScheduleActivityDetails;
  createdAt?: Date | string;
}): string {
  const createdBy =
    (input.createdBy || "Unknown user").trim() || "Unknown user";
  const sourceLabel =
    SCHEDULE_ACTIVITY_SOURCE_LABELS[input.source] || input.source;
  const when = formatScheduleActivityTimestamp(input.createdAt);
  const action = input.isUpdate ? "updated" : "created";
  const lines: string[] = [
    `Schedule ${action} by ${createdBy} on ${when} via ${sourceLabel}.`,
  ];

  const currentDisplay = formatScheduleDateTimeDisplay(
    input.details.scheduleDate,
    input.details.checkIn
  );
  const previousDisplay = formatScheduleDateTimeDisplay(
    input.details.previousScheduleDate,
    input.details.previousCheckIn
  );
  const dateTimeChanged = scheduleSlotChanged(
    input.details.previousScheduleDate,
    input.details.previousCheckIn,
    input.details.scheduleDate,
    input.details.checkIn
  );

  if (
    input.isUpdate &&
    dateTimeChanged &&
    previousDisplay &&
    currentDisplay
  ) {
    lines.push(`Rescheduled from ${previousDisplay} to ${currentDisplay}.`);
  } else if (currentDisplay) {
    lines.push(`Appointment: ${currentDisplay}.`);
  }

  const detailParts = [
    input.details.visitType ? `Visit: ${input.details.visitType}` : "",
    input.details.location ? `Location: ${input.details.location}` : "",
    input.details.resourceName ? `Resource: ${input.details.resourceName}` : "",
  ].filter(Boolean);

  if (detailParts.length) {
    lines.push(detailParts.join(" | "));
  }

  return lines.join(" ");
}

function scheduleSlotChanged(
  previousDate?: string,
  previousTime?: string,
  currentDate?: string,
  currentTime?: string
): boolean {
  const prevDateKey = normalizeScheduleDateKey(previousDate);
  const curDateKey = normalizeScheduleDateKey(currentDate);
  if (prevDateKey !== curDateKey) {
    return true;
  }

  const prevTimeKey = normalizeScheduleTimeKey(previousTime);
  const curTimeKey = normalizeScheduleTimeKey(currentTime);
  if (!prevTimeKey && !curTimeKey) {
    return false;
  }
  return prevTimeKey !== curTimeKey;
}

function normalizeScheduleDateKey(dateRaw?: string): string {
  const trimmed = (dateRaw || "").trim();
  if (!trimmed) {
    return "";
  }
  const dateOnly = trimmed.split("T")[0];
  const dt = DateTime.fromISO(dateOnly);
  return dt.isValid ? dt.toFormat("yyyy-MM-dd") : dateOnly;
}

function normalizeScheduleTimeKey(timeRaw?: string): string {
  const parsed = parseScheduleTime(timeRaw || "");
  return parsed ? parsed.toFormat("HH:mm") : "";
}

function parseScheduleTime(timeRaw: string): DateTime | null {
  const trimmed = timeRaw.trim();
  if (!trimmed) {
    return null;
  }
  for (const format of ["h:mm a", "hh:mm a", "H:mm", "HH:mm"]) {
    const dt = DateTime.fromFormat(trimmed, format);
    if (dt.isValid) {
      return dt;
    }
  }
  return null;
}

function formatScheduleDateTimeDisplay(
  scheduleDate?: string,
  checkIn?: string
): string {
  const dateKey = normalizeScheduleDateKey(scheduleDate);
  if (!dateKey) {
    return "";
  }

  const displayDate = DateTime.fromISO(dateKey).toFormat("MM/dd/yyyy");
  const timeParsed = parseScheduleTime(checkIn || "");
  if (!timeParsed) {
    return displayDate;
  }

  return `${displayDate} at ${timeParsed.toFormat("h:mm a")}`;
}

function formatScheduleActivityTimestamp(value?: Date | string): string {
  if (!value) {
    return DateTime.now().toFormat("MMM d, yyyy 'at' h:mm a");
  }
  const dt =
    value instanceof Date
      ? DateTime.fromJSDate(value)
      : DateTime.fromISO(String(value));
  if (!dt.isValid) {
    return DateTime.now().toFormat("MMM d, yyyy 'at' h:mm a");
  }
  return dt.toFormat("MMM d, yyyy 'at' h:mm a");
}

export function activityNoteBody(note: {
  notes?: string;
  comment?: string;
}): string {
  return String(note.notes ?? note.comment ?? "").trim();
}

/** Backend may auto-create a stub row with only `activityType` and no body. */
export function isEmptyScheduleCreatedActivity(
  note: {
    activityType?: string;
    notes?: string;
    comment?: string;
    encounterId?: string | null;
  },
  encounterId?: string
): boolean {
  const type = String(note.activityType || "").trim();
  if (type !== SCHEDULE_CREATED_ACTIVITY_TYPE) {
    return false;
  }
  const eid = String(note.encounterId || "").trim();
  const targetEid = String(encounterId || "").trim();
  if (targetEid && eid && eid !== targetEid) {
    return false;
  }
  const body = activityNoteBody(note);
  return !body || body === type;
}

export function findEmptyScheduleCreatedStub<
  T extends {
    id?: string;
    activityType?: string;
    notes?: string;
    comment?: string;
    encounterId?: string | null;
    createdAt?: string;
  }
>(activities: T[], encounterId: string): T | null {
  const stubs = activities.filter((activity) =>
    isEmptyScheduleCreatedActivity(activity, encounterId)
  );
  if (!stubs.length) {
    return null;
  }
  return stubs.sort(
    (a, b) =>
      new Date(b.createdAt || 0).getTime() -
      new Date(a.createdAt || 0).getTime()
  )[0];
}
